import { api } from "./api";
import { decimalUnits, type Order } from "./procurement";
import { receivable, unitsString } from "./procurementDifferences";

export type HeaderField = "dobavljac_naziv" | "dobavljac_oib" | "broj" | "datum" | "broj_narudzbe";
export interface DeliverySlipLine {
  redak: number; naziv: string; kolicina: string; jedinica: string; razred_betona: string | null;
  stavka_id: string | null; sigurnost: string; sigurnost_polja: Record<string, string>;
  preostalo: string | null; stvarno_stiglo: string; odstupanje: string | null;
  usporedba: "odgovara" | "manjak" | "visak" | "nije_naruceno";
  nesigurno: boolean; stanje: string;
}
export interface DeliverySlip {
  id: string; dokument_id: string; gradiliste_id: string; dobavljac_id: string | null;
  narudzba_id: string | null; predlozena_narudzba_id: string | null; primitak_id: string | null;
  broj: string | null; datum: string | null; status: "procitano" | "nepovezano" | "potvrdeno" | "odbaceno";
  povezivanje: "povezano" | "prijedlog" | "nepovezano"; prag_sigurnosti: string;
  izvuceni_json: Partial<Record<HeaderField, string | null>> & { sigurnost_polja: Record<string, string>; stavke: unknown[] };
  dokument_url: string; stavke: DeliverySlipLine[]; nesigurna_polja: HeaderField[];
  upozorenje_broj_narudzbe: string | null;
  nedostajuce_stavke: { stavka_id: string; naziv: string; preostalo: string }[];
  beton_prefill: { redak: number; stavka_id: string | null; broj_otpremnice: string | null; m3: string; razred_betona: string | null }[];
}
export interface ReviewedLine {
  redak: number; stavka_id: string | null; kolicina: string; potvrdeno: boolean;
  referentna_stavka_id?: string | null; opis?: string | null;
}
export interface DeliverySlipConfirmInput {
  odrediste?: "gradiliste" | "ured";
  broj_otpremnice: string; datum: string; nesigurna_polja_potvrdena: boolean;
  stavke: ReviewedLine[]; napomena?: string | null;
}
const root = "/nabava/otpremnice-ai";
export const isDeliverySlipOrder = (order: { stanje: string; kategorija?: string }) => ["poslano", "djelomicno_stiglo"].includes(order.stanje) && order.kategorija !== "usluga";
export const deliverySlips = {
  read: async (input: FormData) => (await api.post<DeliverySlip>(root, input)).data,
  list: async (params: { gradiliste_id?: string; nepovezane?: boolean; limit?: number; offset?: number } = {}, signal?: AbortSignal) => (await api.get<DeliverySlip[]>(root, { params, signal })).data,
  detail: async (id: string, signal?: AbortSignal) => (await api.get<DeliverySlip>(`${root}/${id}`, { signal })).data,
  link: async (id: string, narudzba_id: string) => (await api.post<DeliverySlip>(`${root}/${id}/povezi`, { narudzba_id })).data,
  confirm: async (id: string, input: DeliverySlipConfirmInput) => (await api.post<DeliverySlip>(`${root}/${id}/potvrdi`, input)).data,
};

/** Split slip rows are compared together, just as the backend compares them. */
export function compareDeliveryRows(order: Order, rows: ReviewedLine[]) {
  const totals = new Map<string, bigint>();
  for (const row of rows) if (row.stavka_id) totals.set(row.stavka_id, (totals.get(row.stavka_id) ?? 0n) + (decimalUnits(row.kolicina, 3) ?? 0n));
  return order.stavke.map(item => {
    const total = totals.get(item.id) ?? 0n, left = decimalUnits(receivable(order, item), 3) ?? 0n;
    const delta = total - left;
    return { item, total, left, kind: delta === 0n ? "odgovara" as const : delta > 0n ? "visak" as const : "manjak" as const, difference: unitsString(delta < 0n ? -delta : delta), missing: !totals.has(item.id) };
  });
}
